/**
 * Delivery options — the pure rules behind "what will checkout offer me?":
 * the PDP shipping line and its panel (assets/pdp-delivery.js), the header's
 * ship-to popover (assets/ship-to.js) and the collection "Arrives by" filter
 * (assets/arrives-by-filter.js). No DOM, no network, no clock of its own —
 * every function that needs "now" takes it, so the unit tests can pin it
 * (unit/delivery-core.test.cjs).
 *
 * The data comes from Birch Order Management, which mirrors Shopify's delivery
 * profiles into shop metafields:
 *   birch_om.delivery_profiles   the mirror: plans (zones → our manual rates),
 *                                the clock (zone, cutoff, processing days,
 *                                holidays) and the Shipping Rules checkout runs
 *   birch_om.delivery_snapshots  checkout's own answers for carrier plans
 *                                (Shopify Collective), per price band × state
 *   birch_om.delivery_plan       per product / variant: plan, origin, class, rules
 * OM reads the same data with the same rules in src/delivery/delivery-core.mjs
 * (birch-order-management) — keep the two in step. Checked against prod
 * 2026-10-19: 51/51 checkout delivery windows, 119/119 checkout wordings.
 *
 * Loaded site-wide (deferred) as window.BirchDelivery; module.exports too, so
 * Node can load it for the tests.
 */
/* global module */
(function (root) {
  'use strict';

  var DAY_MS = 86400000;
  var DEFAULT_CUTOFF_HOUR = 12; // Shopify's: orders from 12 pm shop time start the next business day
  var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var has = Object.prototype.hasOwnProperty;

  // ── Regions ────────────────────────────────────────────────────────────────

  /** The 50 states + DC — the set "states not shipped to" is read over. */
  var US_STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
    KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
    MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
    NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
  };

  /** Territories and military addresses Shopify also lists as US provinces. */
  var US_OTHER_REGIONS = {
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', GU: 'Guam', AS: 'American Samoa',
    MP: 'Northern Mariana Islands', PW: 'Palau', FM: 'Micronesia', MH: 'Marshall Islands',
    AA: 'Armed Forces Americas', AE: 'Armed Forces Europe', AP: 'Armed Forces Pacific'
  };

  // First three ZIP digits → state, inclusive ranges (USPS prefix allocation;
  // prefixes never split across states except the Pacific 969xx block, which
  // zipToState resolves on five digits). Copied from birch-ai
  // packages/shared/src/shipping.ts zipToRegion — the chat's shipping answers
  // and OM's delivery core use the same table; keep all three in step.
  var ZIP3 = [
    [5, 5, 'NY'], [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'],
    [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'], [50, 54, 'VT'], [55, 55, 'MA'],
    [56, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'], [90, 99, 'AE'],
    [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
    [200, 200, 'DC'], [201, 201, 'VA'], [202, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'],
    [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'],
    [300, 319, 'GA'], [320, 339, 'FL'], [340, 340, 'AA'], [341, 349, 'FL'], [350, 369, 'AL'],
    [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'],
    [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
    [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [569, 569, 'DC'], [570, 577, 'SD'],
    [580, 588, 'ND'], [590, 599, 'MT'],
    [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
    [700, 714, 'LA'], [716, 729, 'AR'], [730, 731, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'],
    [750, 799, 'TX'],
    [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'],
    [870, 884, 'NM'], [885, 885, 'TX'], [889, 898, 'NV'],
    [900, 961, 'CA'], [962, 966, 'AP'], [967, 968, 'HI'], [969, 969, 'GU'], [970, 979, 'OR'],
    [980, 994, 'WA'], [995, 999, 'AK']
  ];

  /** US ZIP (5 digits, or ZIP+4) → state / territory code, or null. */
  function zipToState(zip) {
    var m = /^(\d{5})(?:-?\d{4})?$/.exec(String(zip == null ? '' : zip).trim());
    if (!m) return null;
    var five = m[1];
    if (five === '96799') return 'AS';
    if (five.slice(0, 3) === '969') {
      var n = Number(five.slice(3));
      if (n >= 50 && n <= 52) return 'MP';
      if (n === 40) return 'PW';
      if (n >= 41 && n <= 44) return 'FM';
      if (n >= 60 && n <= 70) return 'MH';
      return 'GU';
    }
    var p = Number(five.slice(0, 3));
    for (var i = 0; i < ZIP3.length; i++) if (p >= ZIP3[i][0] && p <= ZIP3[i][1]) return ZIP3[i][2];
    return null;
  }

  /** The first five digits of a ZIP / ZIP+4, or ''. */
  function zip5(zip) {
    var m = /^(\d{5})(?:-?\d{4})?$/.exec(String(zip == null ? '' : zip).trim());
    return m ? m[1] : '';
  }

  /** "NJ" → "New Jersey"; an unknown code comes back as given. */
  function stateName(code) {
    var c = String(code || '').toUpperCase();
    return US_STATES[c] || US_OTHER_REGIONS[c] || c;
  }

  // ── PO boxes ───────────────────────────────────────────────────────────────
  // Ported from birch-order-management extensions/shipping-rules/src/engine.js
  // (isPoBoxAddress + normAddr) — the Shipping Rules checkout Function, so a
  // saved address reads as a PO box here exactly when checkout will treat it as
  // one. Tight on purpose: every pattern needs the literal word that makes it a
  // box, which keeps "1 Post Office Square" and "Boxwood Lane" out. Keep in step.

  /** Lowercase, every punctuation run → one space: "P.O. Box 12" → "po box 12". */
  function normAddr(value) {
    return typeof value === 'string' ? value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() : '';
  }

  var PO_BOX_PATTERNS = [
    /\bp\s?o\s?(?:box|bin|drawer)\b/, //   po box / p o box / pobox / po drawer
    /\bp\s?o\s?b\b/, //                    p.o.b.
    /\bpost(?:al)?\s?(?:office\s)?box\b/, // post office box / postal box / post box
    /\bbox\s?holder\b/ //                  "box holder" is a PO-box-only addressee
  ];
  /** A field that is nothing but "Box 214" — a PO box written the lazy way. */
  var BARE_BOX = /^box\s?\d+$/;

  /** True when address1, address2 or company looks like a PO box. */
  function isPoBoxAddress(address) {
    if (!address) return false;
    var fields = [address.address1, address.address2, address.company];
    for (var i = 0; i < fields.length; i++) {
      var flat = normAddr(fields[i]);
      if (!flat) continue;
      if (BARE_BOX.test(flat)) return true;
      for (var j = 0; j < PO_BOX_PATTERNS.length; j++) if (PO_BOX_PATTERNS[j].test(flat)) return true;
    }
    return false;
  }

  /** APO/FPO/DPO mail: Shopify files it under the AA / AE / AP provinces. */
  function isMilitaryState(state) {
    var s = String(state || '').toUpperCase();
    return s === 'AA' || s === 'AE' || s === 'AP';
  }

  // ── Days ───────────────────────────────────────────────────────────────────
  // Days are "YYYY-MM-DD" strings; arithmetic runs on UTC midnights, so a
  // clock change never moves a date.

  function addDays(day, n) {
    return new Date(Date.parse(day + 'T00:00:00Z') + n * DAY_MS).toISOString().slice(0, 10);
  }

  function weekday(day) {
    return new Date(Date.parse(day + 'T00:00:00Z')).getUTCDay();
  }

  function isWeekend(day) {
    var wd = weekday(day);
    return wd === 0 || wd === 6;
  }

  function holidaySet(holidays) {
    var off = {};
    if (holidays && holidays.length) for (var i = 0; i < holidays.length; i++) off[holidays[i]] = true;
    return off;
  }

  /**
   * The calendar day and hour of an instant in an IANA zone (the shop's — the
   * clock checkout counts on, wherever the shopper is). Unknown zone → this
   * device's clock, as assets/shipping-delivery-dates.js does.
   */
  function zonedNow(ms, timeZone) {
    var at = new Date(ms);
    if (timeZone) {
      try {
        var parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        }).formatToParts(at);
        var get = function (type) {
          for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value;
          return '';
        };
        var day = get('year') + '-' + get('month') + '-' + get('day');
        var hour = parseInt(get('hour'), 10) % 24;
        var minute = parseInt(get('minute'), 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(day) && hour === hour) return { day: day, hour: hour, minute: minute === minute ? minute : 0 };
      } catch (_err) {
        // An unknown zone: fall through to this device's clock.
      }
    }
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    return { day: at.getFullYear() + '-' + pad(at.getMonth() + 1) + '-' + pad(at.getDate()), hour: at.getHours(), minute: at.getMinutes() };
  }

  /**
   * The day an order placed at `now` starts processing: that day when it is a
   * business day, not a holiday and before the cutoff, else the next day that is.
   * `now` is on the SHIPPING LOCATION's clock (originZone); `cutoffMinute`
   * puts the cutoff inside the hour.
   */
  function startDay(now, cutoffHour, holidays, cutoffMinute) {
    var off = holidaySet(holidays);
    var open = function (d) { return !isWeekend(d) && !off[d]; };
    var cutoff = (cutoffHour == null ? DEFAULT_CUTOFF_HOUR : cutoffHour) * 60 + (cutoffMinute || 0);
    if (open(now.day) && now.hour * 60 + (now.minute || 0) < cutoff) return now.day;
    var d = addDays(now.day, 1);
    while (!open(d)) d = addDays(d, 1);
    return d;
  }

  /**
   * `day` plus `n` business days. With `holidays` those are skipped too — in
   * processing AND transit, and they are the shipping LOCATION's
   * (locationHolidays): checkout skipped Tue 2026-11-03, NJ's Election Day,
   * on all 22 NJ Warehouse windows crossing it and on none of the WA Shop
   * location's; from WA it skipped Wed Nov 11 in transit (OM parity 10-27).
   */
  function addBusinessDays(day, n, holidays) {
    var off = holidaySet(holidays);
    var d = day;
    for (var left = n; left > 0; ) {
      d = addDays(d, 1);
      if (!isWeekend(d) && !off[d]) left--;
    }
    return d;
  }

  /**
   * The zone an order's processing day is counted in: the shipping location's
   * (`at`, birch_om.delivery_plan; its zone from the mirror's `locations`),
   * else the shop's. Prod checkouts 2026-10-20 13:52 ET / 10:52 PT: from WA a
   * General item started that day, from NJ a Fast Shipping item the next —
   * Shopify's cutoff is in "the local timezone of the shipping origin".
   */
  function originZone(mirror, at) {
    var locs = (mirror && mirror.locations) || {};
    return (at != null && locs[String(at)] && locs[String(at)].tz) || (mirror && mirror.timeZone) || '';
  }

  /**
   * The days location `at` is closed: the mirror's shared `holidays` plus its
   * own state's (`locations[at].holidays`, OM's DELIVERY_HOLIDAYS "us-state").
   * Null `at`: the shared ones.
   */
  function locationHolidays(mirror, at) {
    var shared = (mirror && mirror.holidays) || [];
    var locs = (mirror && mirror.locations) || {};
    var own = at != null && locs[String(at)] && locs[String(at)].holidays;
    return own && own.length ? shared.concat(own) : shared;
  }

  /**
   * Checkout's delivery window for a dated rate, for an order placed at `nowMs`
   * and shipped from location `at`: start (+ processing days) = hand-off, then
   * the rate's transit days — weekends and that location's holidays skipped
   * throughout. Null when the mirror can't promise dates.
   */
  function deliveryWindow(nowMs, mirror, transit, at) {
    if (!mirror || !isTransit(transit) || mirror.processingDays == null) return null;
    var off = locationHolidays(mirror, at);
    var start = startDay(zonedNow(nowMs, originZone(mirror, at)), mirror.cutoffHour, off, mirror.cutoffMinute);
    var handoff = addBusinessDays(start, mirror.processingDays, off);
    return { start: start, handoff: handoff, from: addBusinessDays(handoff, transit[0], off), to: addBusinessDays(handoff, transit[1], off) };
  }

  /**
   * The last day an order placed at `nowMs` arrives by when it arrives within
   * `days` business days of its start day (a birch_om.arrives_in day count):
   * processing days, then the rest as transit. Null when the mirror has no
   * processing time, or `days` leaves no room for transit.
   */
  function arrivesBy(nowMs, mirror, days) {
    if (!mirror || mirror.processingDays == null || !(days >= mirror.processingDays)) return null;
    var start = startDay(zonedNow(nowMs, mirror.timeZone), mirror.cutoffHour, mirror.holidays, mirror.cutoffMinute);
    var handoff = addBusinessDays(start, mirror.processingDays, mirror.holidays);
    return addBusinessDays(handoff, days - mirror.processingDays, mirror.holidays);
  }

  /** The delivery region (birch_om.delivery_profiles `regions`) a state is in, or null. */
  function regionOf(regions, state) {
    var s = String(state || '').toUpperCase();
    if (!regions) return null;
    for (var key in regions) if (has.call(regions, key) && Array.isArray(regions[key]) && regions[key].indexOf(s) !== -1) return key;
    return null;
  }

  /**
   * The "Arrives by" choices for a state, from the birch_om.arrives_in
   * filter's values in view ([{value: "CT:4", count}]): one per distinct day
   * count of the state's region, ascending, each with the values it ticks
   * (every one up to that many days — the filter's OR logic) and their summed
   * count (exact: a product carries one value per region). [] when the state
   * is in no region.
   */
  function arrivesChoices(values, regions, state) {
    var region = regionOf(regions, state);
    if (!region) return [];
    var mine = [];
    for (var i = 0; i < (values || []).length; i++) {
      var m = /^([A-Z]{2}):(\d+)$/.exec(String(values[i].value || ''));
      if (m && m[1] === region) mine.push({ days: Number(m[2]), count: Number(values[i].count) || 0, value: values[i].value });
    }
    mine.sort(function (a, b) { return a.days - b.days; });
    var out = [];
    var total = 0;
    var ticked = [];
    for (var j = 0; j < mine.length; j++) {
      total += mine[j].count;
      ticked.push(mine[j].value);
      out.push({ days: mine[j].days, count: total, values: ticked.slice() });
    }
    return out;
  }

  /** The most days among arrives_in values ("CT:4" → 4), or null — what an applied filter means. */
  function arrivesDays(values) {
    var best = null;
    for (var i = 0; i < (values || []).length; i++) {
      var m = /:(\d+)$/.exec(String(values[i] || ''));
      if (m && (best == null || Number(m[1]) > best)) best = Number(m[1]);
    }
    return best;
  }

  /** "2026-10-22" → "Thu, Oct 22". */
  function formatDay(day) {
    var d = new Date(Date.parse(day + 'T00:00:00Z'));
    return WEEKDAYS[d.getUTCDay()] + ', ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /**
   * A window in checkout's words, as assets/shipping-delivery-dates.js has it:
   * "Thu, Oct 22" for one day, "Oct 22 – 27" within a month, "Oct 30 – Nov 3"
   * across one.
   */
  function formatWhen(from, to) {
    if (from === to) return formatDay(from);
    var a = new Date(Date.parse(from + 'T00:00:00Z'));
    var b = new Date(Date.parse(to + 'T00:00:00Z'));
    var md = function (d) { return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate(); };
    return a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear()
      ? md(a) + ' – ' + b.getUTCDate()
      : md(a) + ' – ' + md(b);
  }

  /** 12 → "12 PM", 9 → "9 AM", 0 → "12 AM". */
  function hourText(h) {
    var n = Number(h) % 24;
    return (n % 12 === 0 ? 12 : n % 12) + (n < 12 ? ' AM' : ' PM');
  }

  var ZONE_LABELS = {
    'America/New_York': 'ET', 'America/Detroit': 'ET', 'America/Chicago': 'CT', 'America/Denver': 'MT',
    'America/Phoenix': 'MT', 'America/Los_Angeles': 'PT', 'America/Anchorage': 'AKT', 'Pacific/Honolulu': 'HT'
  };

  /** A short name for the shop's zone, for "Order by 12 PM ET". */
  function zoneLabel(timeZone, nowMs) {
    if (ZONE_LABELS[timeZone]) return ZONE_LABELS[timeZone];
    try {
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: timeZone, timeZoneName: 'short' }).formatToParts(new Date(nowMs || 0));
      for (var i = 0; i < parts.length; i++) if (parts[i].type === 'timeZoneName') return parts[i].value;
    } catch (_err) {
      // Unknown zone: no label rather than a wrong one.
    }
    return '';
  }

  // ── Words ──────────────────────────────────────────────────────────────────

  function isTransit(t) {
    return !!t && t.length === 2 && typeof t[0] === 'number' && typeof t[1] === 'number' && t[0] >= 0 && t[1] >= t[0];
  }

  /** Checkout's words for a transit time: "3 to 5 business days", "2 business days", "1 business day". */
  function transitWords(transit) {
    if (!isTransit(transit)) return '';
    var a = transit[0];
    var b = transit[1];
    if (a === b) return a === 1 ? '1 business day' : a + ' business days';
    return a + ' to ' + b + ' business days';
  }

  /**
   * What checkout writes under an undated rate: its description, else its
   * transit time. A supplier's date-range description (`dated`) is only true
   * on the day it was fetched, so it is dropped unless `fresh`.
   */
  function optionWords(option, fresh) {
    if (!option) return '';
    var desc = String(option.desc || '').trim();
    if (desc && option.dated && !fresh) return '';
    return desc || transitWords(option.transit);
  }

  /** Cents → "Free", "$25", "$9.99". */
  function priceText(cents) {
    var c = Math.round(Number(cents) || 0);
    if (c === 0) return 'Free';
    var rest = c % 100;
    return '$' + Math.floor(c / 100) + (rest ? '.' + (rest < 10 ? '0' : '') + rest : '');
  }

  /** "Standard" goes unsaid — it is the plain claim; any other name is part of it. */
  function nameBit(name) {
    return /^standard$/i.test(String(name || '').trim()) ? '' : String(name || '').trim();
  }

  /**
   * The trust-bar line for one described option (see describe): "Free Delivery
   * Oct 27 – 29", "Free Express Delivery Oct 23 – 27", "$25 Express Delivery
   * Thu, Oct 22" when dated; "Free Shipping · 3 to 5 business days", "Free
   * Shipping" otherwise.
   */
  function lineText(option) {
    var c = Math.round(Number(option.price) || 0);
    var n = String(option.name || '').trim();
    var price = priceText(c);
    if (option.delivery) return [price, nameBit(n), 'Delivery', option.delivery.when].filter(Boolean).join(' ');
    var head = /shipping|delivery/i.test(n)
      ? c === 0 ? (/free/i.test(n) ? n : 'Free ' + n) : price + ' ' + n
      : [price, nameBit(n), 'Shipping'].filter(Boolean).join(' ');
    return head + (option.words ? ' · ' + option.words : '');
  }

  // ── Zones, bands, rules ────────────────────────────────────────────────────

  /** Does a zone's `states` ("*" | {only} | {except}) cover a province code? */
  function statesInclude(states, state) {
    if (states === '*') return true;
    if (states && Array.isArray(states.only)) return states.only.indexOf(state) !== -1;
    if (states && Array.isArray(states.except)) return states.except.indexOf(state) === -1;
    return false;
  }

  /** The first zone of a plan covering `state`, or null (checkout offers nothing there). */
  function zoneFor(plan, state) {
    var zones = (plan && plan.zones) || [];
    for (var i = 0; i < zones.length; i++) if (zones[i] && statesInclude(zones[i].states, state)) return zones[i];
    return null;
  }

  /** The band of a class snapshot a price falls in: the last with from ≤ price. */
  function bandFor(snapshot, priceCents) {
    var hit = null;
    var bands = (snapshot && snapshot.bands) || [];
    for (var i = 0; i < bands.length; i++) if (bands[i] && bands[i].from <= priceCents) hit = bands[i];
    return hit;
  }

  /** Options a single-item cart at `priceCents` is offered: price conditions applied, order kept. */
  function usableOptions(options, priceCents) {
    return (options || []).filter(function (o) {
      return o && (o.minSubtotal == null || priceCents >= o.minSubtotal) && (o.maxSubtotal == null || priceCents <= o.maxSubtotal);
    });
  }

  function upperList(list) {
    return Array.isArray(list) ? list.map(function (s) { return String(s).trim().toUpperCase(); }) : [];
  }

  /**
   * Does an address satisfy a Shipping Rule's `when`? Same reading as OM's
   * engine.js addressMatches: every populated condition must hold, and an
   * empty `when` matches nothing unless it says `always`. `address` is what the
   * ship-to location knows — {state, zip, po}; a ZIP condition never matches
   * a location without a ZIP.
   */
  function ruleMatches(when, address) {
    if (!when || !address) return false;
    var state = String(address.state || '').toUpperCase();
    var zip = String(address.zip || '').replace(/\s/g, '');
    var provinces = upperList(when.provinces);
    var notProvinces = upperList(when.notProvinces);
    var prefixes = Array.isArray(when.zipPrefixes) ? when.zipPrefixes.map(String) : [];
    if (when.poBox && !address.po) return false;
    if (when.military && !isMilitaryState(state)) return false;
    if (provinces.length && provinces.indexOf(state) === -1) return false;
    if (notProvinces.length && notProvinces.indexOf(state) !== -1) return false;
    if (prefixes.length && !prefixes.some(function (p) { return zip && zip.indexOf(p) === 0; })) return false;
    var asked = when.poBox || when.military || provinces.length || notProvinces.length || prefixes.length;
    return asked ? true : !!when.always;
  }

  function eachRule(rules, action, fn) {
    if (!rules) return;
    for (var key in rules) if (has.call(rules, key) && rules[key] && rules[key].action === action) fn(rules[key], key);
  }

  /** Does the product carry a hide rule (so the live check is worth asking)? */
  function hasHideRule(rules) {
    var found = false;
    eachRule(rules, 'hide', function () { found = true; });
    return found;
  }

  /**
   * Drop the options a matching hide rule hides (by exact lowercase title, or
   * title fragment). As in OM's engine.js, a rule that would leave nothing to
   * pick is dropped whole — checkout never empties a delivery group for one.
   */
  function applyHideRules(options, rules, address) {
    var out = options || [];
    eachRule(rules, 'hide', function (rule) {
      if (!ruleMatches(rule.when, address)) return;
      var titles = (rule.titles || []).map(function (t) { return String(t).trim().toLowerCase(); });
      var parts = (rule.titleContains || []).map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
      var kept = out.filter(function (o) {
        var title = String(o.name || '').trim().toLowerCase();
        if (titles.indexOf(title) !== -1) return false;
        for (var i = 0; i < parts.length; i++) if (title.indexOf(parts[i]) !== -1) return false;
        return true;
      });
      if (kept.length) out = kept;
    });
    return out;
  }

  /** The product's block rules: every message, and whether it stops this address. */
  function blockRules(rules, address) {
    var out = [];
    eachRule(rules, 'block', function (rule, key) {
      if (rule.message) out.push({ key: key, message: String(rule.message), applies: ruleMatches(rule.when, address) });
    });
    return out;
  }

  // ── Resolve ────────────────────────────────────────────────────────────────

  /**
   * One option as the page shows it. `delivery` = Shopify's estimated delivery
   * dates, only for a line shipped from one of OUR locations, on one of OUR
   * rates with a transit time, while the shop has dates on (`datable`);
   * otherwise `words`. Stock doesn't matter: checkout dated an oversold (-1)
   * and an untracked variant like any other (prod 2026-10-20). `at` = the
   * location it ships from, whose clock sets the cutoff.
   */
  function describe(option, mirror, datable, fresh, nowMs, at) {
    var out = {
      name: String(option.name || '').trim(),
      price: Math.round(Number(option.price) || 0),
      desc: String(option.desc || '').trim(),
      ours: option.ours !== false,
      delivery: null,
      words: ''
    };
    var w = datable && out.ours && isTransit(option.transit) ? deliveryWindow(nowMs, mirror, option.transit, at) : null;
    if (w) out.delivery = { from: w.from, to: w.to, when: formatWhen(w.from, w.to) };
    else out.words = optionWords(option, fresh);
    return out;
  }

  /**
   * What checkout offers one variant at one location.
   *
   * input   {mirror, plan, origin, at, snapshot, price, rules, live}
   *         mirror    the delivery_profiles mirror (its clock fields are read)
   *         plan      the variant's Plan from the mirror
   *         origin    "own" | "supplier" (birch_om.delivery_plan)
   *         at        the location it ships from (birch_om.delivery_plan), or null
   *         snapshot  its class's snapshot ({bands}) or null
   *         productSnapshot  this product's own answer for the variant's price
   *                   range when its supplier rates it apart from the rest of
   *                   the class ({day, options}: the entry of
   *                   birch_om.delivery_snapshots `products` whose range holds
   *                   the variant's price, its options read from
   *                   `productSets[entry.set]` by the Liquid), or null
   *         price     variant price, cents (a one-item cart's subtotal)
   *         rules     {key: Rule} — only the rules this product carries
   *         live      {day, options, blocked?} from the live check for this
   *                   ZIP, or null
   * loc     {state, zip?, po?} — the ship-to location
   *
   * Options: the live answer when there is one (checkout's own, taken as is);
   * else a carrier plan's snapshot set for the price band and state; else the
   * state's zone. Then price conditions and matching hide rules. The line is
   * the first option — checkout's preselected, cheapest one.
   *
   * `blocked`: the message checkout refuses this address with — the live
   * check's `blocked`, else the first of the product's block rules the
   * address matches; then no options are offered and the line says so.
   * `source` says where the options came from: "live", "product", "snapshot" or "zone"
   * (a carrier plan answered from its zone means the snapshot had no entry
   * for this band and state).
   *
   * Null without a plan or a state; the caller keeps the server's text.
   */
  function resolve(input, loc, nowMs) {
    var state = loc && loc.state ? String(loc.state).trim().toUpperCase() : '';
    if (!input || !input.plan || !state) return null;
    var mirror = input.mirror || {};
    var today = zonedNow(nowMs, mirror.timeZone).day;
    var address = { state: state, zip: zip5(loc.zip), po: !!loc.po };
    var price = Math.round(Number(input.price) || 0);
    var list = null;
    var fresh = false;
    var source = 'zone';
    var blocked = '';

    if (input.live && Array.isArray(input.live.options)) {
      list = input.live.options;
      fresh = input.live.day === today;
      source = 'live';
      blocked = input.live.blocked ? String(input.live.blocked) : '';
    } else {
      var band = input.plan.carrier && input.snapshot ? bandFor(input.snapshot, price) : null;
      var own = input.plan.carrier && input.productSnapshot && Array.isArray(input.productSnapshot.options) ? input.productSnapshot : null;
      if (band && band.states && has.call(band.states, state)) {
        var i = band.states[state];
        // The class says whether it ships there; a product rated apart says what.
        if (i >= 0 && own) {
          list = own.options;
          fresh = own.day === today;
          source = 'product';
        } else {
          list = i >= 0 && band.sets && band.sets[i] ? band.sets[i] : [];
          fresh = band.day === today;
          source = 'snapshot';
        }
      } else if (own && zoneFor(input.plan, state)) {
        list = own.options;
        fresh = own.day === today;
        source = 'product';
      }
      if (!list) {
        var zone = zoneFor(input.plan, state);
        list = zone ? zone.options || [] : [];
      }
      list = applyHideRules(usableOptions(list, price), input.rules, address);
    }

    var blocks = blockRules(input.rules, address);
    if (!blocked) {
      for (var b = 0; b < blocks.length; b++) {
        if (blocks[b].applies) {
          blocked = blocks[b].message;
          break;
        }
      }
    }

    var datable = mirror.datesEnabled === true && mirror.processingDays != null && (input.origin || 'own') === 'own';
    var options = blocked ? [] : list.map(function (o) { return describe(o, mirror, datable, fresh, nowMs, input.at); });

    return {
      state: state,
      zip: address.zip,
      today: today,
      source: source,
      options: options,
      blocks: blocks,
      blocked: blocked,
      ships: options.length > 0,
      line: blocked
        ? "Can't ship to this address"
        : options.length ? lineText(options[0]) : "Doesn't ship to " + stateName(state)
    };
  }

  // ── Cart ───────────────────────────────────────────────────────────────────

  /**
   * The shipment a cart line goes into, as checkout splits an order: one per
   * (delivery profile, location group) of OUR stock, and one per Collective
   * supplier location. 30 of 30 prod checkouts, 2026-10-20: General + Kalon at
   * the same WA location → two shipments (different profiles); two General
   * items at Bellevue and WA → one; Makemake + L'ovedbaby, both in Collective
   * group 0 → two (two suppliers). `from` is the supplier's location
   * (birch_om.delivery_plan); before the sync has written one, the vendor
   * stands in for it.
   */
  function shipmentKey(line) {
    var plan = String(line.planKey || '');
    if ((line.origin || 'own') === 'supplier') return plan + '|s:' + String(line.from || 'v:' + String(line.vendor || '').toLowerCase());
    return plan + '|own';
  }

  function mergeRules(lines) {
    var out = {};
    for (var i = 0; i < lines.length; i++) {
      var r = lines[i].rules || {};
      for (var k in r) if (has.call(r, k)) out[k] = r[k];
    }
    return out;
  }

  /** Of a shipment's own-stock locations, the one whose clock starts processing last (null: the shop's). */
  function latestOrigin(nowMs, mirror, lines) {
    var best = null;
    var bestDay = '';
    for (var i = 0; i < lines.length; i++) {
      var at = lines[i].at == null ? null : String(lines[i].at);
      var day = startDay(zonedNow(nowMs, originZone(mirror, at)), mirror.cutoffHour, locationHolidays(mirror, at), mirror.cutoffMinute);
      if (i === 0 || day > bestDay) {
        best = at;
        bestDay = day;
      }
    }
    return best;
  }

  /**
   * What checkout offers a whole cart at one location, shipment by shipment.
   *
   * input   {mirror, lines, live}
   *         lines  [{key, variant, quantity, price (the line's total, cents),
   *                  planKey, plan, origin, from, at, vendor, class, snapshot,
   *                  rules, policy, title, ships}] — `ships` false = needs no
   *                  shipping (a gift card); `policy` = the merchant's shipping
   *                  text for it (birch_om.shipping_policy or an OM rule),
   *                  which wins over the computed timing, as on the PDP
   *         live   {shipmentKey: {day, options, blocked}} — the live check's
   *                answers for carrier shipments, by shipmentKey
   * loc     {state, zip?, po?}
   *
   * Each shipment resolves like one product (resolve) with the shipment's
   * subtotal as the price — price conditions and a supplier's price band read
   * the items going together — and every Shipping Rule its lines carry. Dates
   * use the clock of the shipment's latest-starting location. A shipment
   * whose lines all carry merchant text shows that text instead.
   *
   * Returns null without a state or any shipping line; else {state, zip,
   * shipments: [{key, lines: [keys], titles, view, policy, line}], split
   * (more than one shipment), unshippable: [keys] (lines checkout drops at
   * this address)}.
   */
  function resolveCart(input, loc, nowMs) {
    var state = loc && loc.state ? String(loc.state).trim().toUpperCase() : '';
    var mirror = (input && input.mirror) || null;
    var lines = ((input && input.lines) || []).filter(function (l) { return l && l.ships !== false && l.plan; });
    if (!state || !mirror || !lines.length) return null;
    var order = [];
    var groups = {};
    for (var i = 0; i < lines.length; i++) {
      var key = shipmentKey(lines[i]);
      if (!has.call(groups, key)) {
        groups[key] = [];
        order.push(key);
      }
      groups[key].push(lines[i]);
    }
    var live = (input && input.live) || {};
    var shipments = [];
    var unshippable = [];
    for (var g = 0; g < order.length; g++) {
      var members = groups[order[g]];
      var first = members[0];
      var subtotal = 0;
      for (var m = 0; m < members.length; m++) subtotal += Math.round(Number(members[m].price) || 0);
      // A product rated apart speaks for its shipment when it is the only
      // product in it (or every product in it shares that answer); a mix is
      // the live check's to answer.
      var owns = members.map(function (l) { return l.productSnapshot ? JSON.stringify(l.productSnapshot.options) : ''; });
      var sameOwn = owns.every(function (o) { return o === owns[0]; });
      var view = resolve({
        mirror: mirror,
        plan: first.plan,
        origin: first.origin,
        at: (first.origin || 'own') === 'own' ? latestOrigin(nowMs, mirror, members) : null,
        snapshot: first.snapshot || null,
        productSnapshot: sameOwn ? first.productSnapshot || null : null,
        price: subtotal,
        rules: mergeRules(members),
        live: has.call(live, order[g]) ? live[order[g]] : null
      }, loc, nowMs);
      var keys = members.map(function (l) { return l.key; });
      var policies = members.map(function (l) { return String(l.policy || '').trim(); });
      var allPolicy = policies.every(function (t) { return !!t; });
      if (view && !view.ships && !view.blocked) {
        // Checkout drops lines it can't ship to the address (prod: Tiny Land to
        // AK left a one-shipment checkout for the rest).
        unshippable = unshippable.concat(keys);
      }
      shipments.push({
        key: order[g],
        lines: keys,
        titles: members.map(function (l) { return String(l.title || ''); }),
        subtotal: subtotal,
        carrier: !!(first.plan && first.plan.carrier),
        view: view,
        policy: allPolicy ? policies[0] : '',
        line: allPolicy ? policies[0] : view ? view.line : ''
      });
    }
    var shipping = shipments.filter(function (s) { return !s.view || s.view.ships || s.view.blocked; });
    return {
      state: state,
      zip: zip5(loc.zip),
      shipments: shipments,
      split: shipping.length > 1,
      unshippable: unshippable
    };
  }

  /**
   * The states (50 + DC) checkout offers this variant nothing in: a snapshot's
   * -1, else no zone of the plan covering the state.
   */
  function notShippedStates(input) {
    var out = [];
    if (!input || !input.plan) return out;
    var band = input.plan.carrier && input.snapshot ? bandFor(input.snapshot, Math.round(Number(input.price) || 0)) : null;
    for (var code in US_STATES) {
      if (!has.call(US_STATES, code)) continue;
      if (band && band.states && has.call(band.states, code)) {
        if (band.states[code] < 0) out.push(code);
      } else if (!zoneFor(input.plan, code)) {
        out.push(code);
      }
    }
    return out;
  }

  var api = {
    US_STATES: US_STATES,
    US_OTHER_REGIONS: US_OTHER_REGIONS,
    zipToState: zipToState,
    zip5: zip5,
    stateName: stateName,
    isPoBoxAddress: isPoBoxAddress,
    isMilitaryState: isMilitaryState,
    zonedNow: zonedNow,
    startDay: startDay,
    addBusinessDays: addBusinessDays,
    deliveryWindow: deliveryWindow,
    arrivesBy: arrivesBy,
    regionOf: regionOf,
    arrivesChoices: arrivesChoices,
    arrivesDays: arrivesDays,
    formatDay: formatDay,
    formatWhen: formatWhen,
    hourText: hourText,
    zoneLabel: zoneLabel,
    transitWords: transitWords,
    optionWords: optionWords,
    priceText: priceText,
    lineText: lineText,
    statesInclude: statesInclude,
    zoneFor: zoneFor,
    bandFor: bandFor,
    usableOptions: usableOptions,
    ruleMatches: ruleMatches,
    hasHideRule: hasHideRule,
    applyHideRules: applyHideRules,
    blockRules: blockRules,
    describe: describe,
    resolve: resolve,
    originZone: originZone,
    locationHolidays: locationHolidays,
    shipmentKey: shipmentKey,
    resolveCart: resolveCart,
    notShippedStates: notShippedStates
  };

  root.BirchDelivery = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
